import React, { useState, useMemo, useCallback, memo, useEffect, useRef } from 'react';
import { isClient } from 'frontend/helpers/ssr-helper';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Typography,
  IconButton,
  Box,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import CloseIcon from '@mui/icons-material/Close';
import { useColors } from 'frontend/hooks/useColors';
import { FareCard } from './FareCard';
import { PageIndicators } from './PageIndicators';
import { Legend } from './Legend';
import './styles.css';

export const CARD_WIDTH = 320;
const CARD_GAP = 16;

const FareDialog = ({ open, onClose, data = [], currency }) => {
  const theme = useTheme();
  const colorTheme = useColors();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [currentPage, setCurrentPage] = useState(0);
  const [cardsPerPage, setCardsPerPage] = useState(1);
  const containerRef = useRef();

  const calculateCardsPerPage = useCallback(() => {
    if (containerRef.current) {
      const containerWidth = containerRef.current.offsetWidth;
      const computedStyle = isClient && window.getComputedStyle(containerRef.current);
      const paddingLeft = parseFloat(computedStyle.paddingLeft) || 0;
      const paddingRight = parseFloat(computedStyle.paddingRight) || 0;
      const effectiveWidth = containerWidth - paddingLeft - paddingRight;
      const calculatedCards = Math.floor((effectiveWidth + CARD_GAP) / (CARD_WIDTH + CARD_GAP));
      //setCardsPerPage(calculatedCards > 0 ? calculatedCards : 1);
    }
  }, []);

  useEffect(() => {
    if (open) {
      requestAnimationFrame(() => calculateCardsPerPage());
    }
    const handleResize = () => calculateCardsPerPage();
    isClient && window.addEventListener('resize', handleResize);

    return () => {
      isClient && window.removeEventListener('resize', handleResize);
    };
  }, [open, calculateCardsPerPage]);

  const { totalPages, currentFares, isLastPage, showArrows } = useMemo(() => {
    const total = Math.ceil(data.length / cardsPerPage);
    const startIndex =
      currentPage === 0 ? currentPage * cardsPerPage : currentPage * cardsPerPage - 1;
    const currentFares = data.slice(
      startIndex,
      currentPage > 0 ? startIndex + cardsPerPage + 2 : startIndex + cardsPerPage + 1,
    );

    return {
      totalPages: total,
      currentFares,
      isLastPage: currentPage === total - 1,
      showArrows: total > 1,
    };
  }, [data, cardsPerPage, currentPage]);

  const baseFare = useMemo(() => data[0], [data]);

  const handlePrevPage = useCallback(() => {
    setCurrentPage((prev) => (prev > 0 ? prev - 1 : prev));
  }, []);

  const handleNextPage = useCallback(() => {
    setCurrentPage((prev) => (prev < totalPages - 1 ? prev + 1 : prev));
  }, [totalPages]);

  const handlePageChange = useCallback((page) => {
    setCurrentPage(page);
  }, []);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={isMobile}
      maxWidth={false}
      PaperProps={{
        sx: {
          width: '100%',
          maxWidth: { xs: '100%', sm: '42rem' },
          m: { xs: 0, sm: 2 },
          borderRadius: '15px',
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Box component="div">
          <Typography variant="h6">Opciones de Tarifas</Typography>
        </Box>
        <IconButton edge="end" color="inherit" onClick={onClose} aria-label="close">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent
        style={{
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          overflowX: 'hidden',
        }}
      >
        {showArrows && (
          <>
            <IconButton
              onClick={handlePrevPage}
              disabled={!currentPage}
              sx={{
                display: { sm: 'flex', xs: 'none' },
                position: 'absolute',
                left: { xs: '0.5rem', sm: '2rem' },
                top: 'calc(50% - 2rem)',
                transform: 'translateY(-50%)',
              }}
            >
              <ChevronLeft />
            </IconButton>

            <IconButton
              onClick={handleNextPage}
              disabled={isLastPage}
              sx={{
                display: { sm: 'flex', xs: 'none' },
                position: 'absolute',
                right: { xs: '0.5rem', sm: '2rem' },
                top: 'calc(50% - 2rem)',
                transform: 'translateY(-50%)',
              }}
            >
              <ChevronRight />
            </IconButton>
          </>
        )}
        <Box
          sx={{
            py: 2,
            justifyContent: 'center',
            px: { xs: 3, sm: 6 },
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            flexDirection: 'column',
            margin: 'auto',
            overflow: { xs: 'unset', sm: 'hidden' },
            maxWidth: { xs: '21rem', sm: '28rem' },
            marginLeft: { xs: '-1,5rem', sm: 'auto' },
            flex: 1,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              gap: `${CARD_GAP}px`,
              justifyItems: 'center',
              overflow: 'hidden',
            }}
            ref={containerRef}
          >
            {currentPage === 0 && <Box style={{ width: `${CARD_WIDTH}px` }} />}
            {currentFares.map((fare, index) => (
              <FareCard
                currency={currency}
                key={fare.id}
                disabled={currentPage > 0 ? index !== 1 : !!index}
                fare={fare}
                baseFare={baseFare}
                {...colorTheme}
              />
            ))}
            {currentPage === totalPages - 1 && <Box style={{ width: `${CARD_WIDTH}px` }} />}
          </Box>

          {showArrows && (
            <PageIndicators
              handlePrevPage={handlePrevPage}
              handleNextPage={handleNextPage}
              totalPages={totalPages}
              currentPage={currentPage}
              onPageChange={handlePageChange}
              {...colorTheme}
            />
          )}
        </Box>
        <Legend />
      </DialogContent>
    </Dialog>
  );
};

export default memo(FareDialog);
